import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BlogCard, BlogHeader } from '../../components/blog';
import { LatteButton, LatteSpinner, SEOHelmet } from '../../components/common';
import { Footer } from '../../components/layout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../styles/common/blog-components.css';

const POSTS_PER_PAGE = 15;

const BlogListPage = () => {
  const { user } = useAuth();
  const { showError } = useToast();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedTag, setSelectedTag] = useState(null);
  const [allTags, setAllTags] = useState([]);

  const checkAdminStatus = async () => {
    if (!user) {
      //console.log('No user found');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      //console.log('Admin check result:', data, error);
      setIsAdmin(data?.role === 'admin');
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .range(0, POSTS_PER_PAGE - 1);

      if (error) throw error;
      
      setPosts(data || []);
      setHasMore((data || []).length === POSTS_PER_PAGE);
      setPage(1);
    } catch (error) {
      console.error('Error loading posts:', error);
      showError('포스트를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const start = page * POSTS_PER_PAGE;
      const end = start + POSTS_PER_PAGE - 1;

      console.log('Loading more posts:', { start, end, page }); // 디버깅용

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .range(start, end);

      if (error) throw error;

      if (data && data.length > 0) {
        // 중복 제거 로직 추가
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newPosts = data.filter(post => !existingIds.has(post.id));
          return [...prev, ...newPosts];
        });
        setHasMore(data.length === POSTS_PER_PAGE);
        setPage(prev => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more posts:', error);
      showError('추가 포스트를 불러오는데 실패했습니다');
    } finally {
      setLoadingMore(false);
    }
  }, [page, loadingMore, hasMore, showError]);

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [user]); // user가 변경될 때마다 관리자 상태 체크

  useEffect(() => {
    // 태그가 선택되면 필터링
    if (selectedTag) {
      const filtered = posts.filter(post => 
        post.tags && post.tags.includes(selectedTag)
      );
      setFilteredPosts(filtered);
    } else {
      setFilteredPosts(posts);
    }
  }, [selectedTag, posts]);

  useEffect(() => {
    // 모든 태그 추출
    const tags = new Set();
    posts.forEach(post => {
      if (post.tags && Array.isArray(post.tags)) {
        post.tags.forEach(tag => tags.add(tag));
      }
    });
    setAllTags([...tags].sort());
  }, [posts]);

  useEffect(() => {
    let scrollTimeout;
    let isLoading = false;
    
    const handleScroll = () => {
      // 이미 로딩 중이면 무시
      if (isLoading) return;
      
      // debounce 처리 - 200ms 동안 추가 스크롤 이벤트 무시
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (window.innerHeight + document.documentElement.scrollTop 
            >= document.documentElement.offsetHeight - 100) {
          if (!loadingMore && hasMore && !isLoading) {
            isLoading = true;
            loadMorePosts().finally(() => {
              isLoading = false;
            });
          }
        }
      }, 200); // 100ms에서 200ms로 증가
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [loadingMore, hasMore, loadMorePosts]);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <LatteSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SEO 메타 태그 */}
      <SEOHelmet
        title={selectedTag ? `#${selectedTag} 관련 글` : "블로그"}
        description="라떼이즈의 이야기와 인사이트를 공유합니다. AI 이력서, 커리어 컨설팅, 취업 팁 등 다양한 정보를 확인하세요."
        keywords={allTags.length > 0 ? allTags.join(', ') : "AI 이력서, 커리어 컨설팅, 취업 팁, 면접 준비"}
        url="/blog"
        type="website"
      />
      
      <BlogHeader />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 섹션 */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Latteis Blog
                </h1>
                {filteredPosts.length > 0 && !loading && (
                  <span className="text-sm text-gray-400">
                    ( {filteredPosts.length} 건 )
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-1">
                라떼이즈의 이야기와 인사이트를 공유합니다
              </p>
            </div>
            
            {isAdmin && (
              <Link to="/blog/new">
                <button className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors">
                  새 글 작성
                </button>
              </Link>
            )}
          </div>
          <div className="mt-6 h-px bg-gray-200"></div>
        </div>

        {/* 태그 필터 */}
        {allTags.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8">
            <button
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                !selectedTag 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-amber-50 border border-gray-300'
              }`}
              onClick={() => setSelectedTag(null)}
            >
              전체
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedTag === tag 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-amber-50 border border-gray-300'
                }`}
                onClick={() => setSelectedTag(tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}

        {/* 블로그 목록 */}
        {filteredPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {selectedTag ? `#${selectedTag} 태그로 작성된 글이 없습니다` : '아직 작성된 글이 없습니다'}
            </h3>
            <p className="text-gray-500 text-sm">
              {selectedTag ? '다른 태그를 선택해보세요' : '첨 번째 글을 작성해보세요'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map(post => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>

            {loadingMore && (
              <div className="flex justify-center mt-8">
                <LatteSpinner size="md" />
              </div>
            )}
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogListPage;
